import * as pdfjs from 'pdfjs-dist';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

/**
 * Preparing a scanned fingerprint card for the auto-segmenter.
 *
 * The segmenter reads images, not PDFs. A customer who uploaded a PDF got a conversion that failed three seconds
 * later with "Unable to detect image format", was routed to manual review, and — until this was fixed — sat in
 * SUBMITTED indefinitely. Scanners very often produce PDFs, so this is not a rare mistake to design around; the
 * PDF is rasterised in the browser before upload and the segmenter only ever sees a PNG.
 *
 * Rendered at 500 DPI, matching v1: a PDF page carries no pixels of its own, so the scale we choose IS the
 * resolution the segmenter gets, and 500 is the minimum the FBI accepts for a card.
 */

// pdf.js needs its worker; Vite resolves this to a hashed asset URL at build time, so it is served from our own
// origin rather than a CDN the CSP would refuse.
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

/** FBI minimum for a scanned card. A PDF is rendered at this; a raster image is measured against it. */
export const REQUIRED_DPI = 500;
/**
 * An FD-258 card is 8" × 8", so 8 × 500 = 4000 px.
 *
 * v1 accepted 3500 — a 12.5% tolerance for a tight crop. This is the stricter, literal rule; if support starts
 * seeing rejections of cards that are genuinely fine, this constant is the one thing to lower.
 */
export const MIN_CARD_PX = 8 * REQUIRED_DPI;

export interface PreparedImage {
  /** Always a PNG or the original raster file — never a PDF. */
  file: File;
  width: number;
  height: number;
  /** Small data URL for the on-screen preview. */
  thumbnail: string;
  /** Which page of a multi-page PDF this came from, when it came from one. */
  page?: number;
}

export class CardImageError extends Error {}

/** Decode a raster file far enough to measure it. */
async function measure(file: File): Promise<{ bitmap: ImageBitmap; width: number; height: number }> {
  try {
    const bitmap = await createImageBitmap(file);
    return { bitmap, width: bitmap.width, height: bitmap.height };
  } catch {
    throw new CardImageError(`${file.name} could not be read as an image. Save it as PNG, JPEG or TIFF and try again.`);
  }
}

/** Downscale to a preview. Kept small: a 4000px card at full size in an <img> is pointless and slow. */
function makeThumbnail(source: CanvasImageSource, width: number, height: number): string {
  const max = 220;
  const scale = Math.min(max / width, max / height, 1);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  canvas.getContext('2d')?.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.7);
}

const canvasToPng = (canvas: HTMLCanvasElement, name: string): Promise<File> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(new File([blob], name, { type: 'image/png' })) : reject(new CardImageError(`Could not convert ${name}`))),
      'image/png',
    );
  });

/**
 * Rasterise every page of a PDF at 500 DPI.
 *
 * Each page becomes its own image, because a customer scanning both sides of a card produces a two-page PDF and
 * both sides carry prints. A 8.5×11 page at 500 DPI is roughly 4250×5500 — large, but within canvas limits, and
 * anything smaller would defeat the resolution check that follows.
 */
export async function pdfToPngs(file: File): Promise<File[]> {
  let pdf;
  try {
    pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  } catch {
    throw new CardImageError(`${file.name} could not be opened as a PDF. If it is password-protected, remove the password and try again.`);
  }
  const base = file.name.replace(/\.pdf$/i, '');
  const out: File[] = [];
  for (let n = 1; n <= pdf.numPages; n++) {
    const page = await pdf.getPage(n);
    const viewport = page.getViewport({ scale: REQUIRED_DPI / 72 }); // PDF user space is 72 units per inch
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) throw new CardImageError('This browser could not render the PDF.');
    // White behind the page: a PDF with a transparent background otherwise rasterises onto black, and the
    // segmenter is looking for dark ridges on a light card.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport }).promise;
    out.push(await canvasToPng(canvas, pdf.numPages > 1 ? `${base}-page-${n}.png` : `${base}.png`));
    // Free the backing store before the next page; several 4000px canvases at once is what exhausts memory.
    canvas.width = canvas.height = 0;
  }
  return out;
}

/**
 * Turn whatever the customer picked into card images we can actually convert, or explain why not.
 *
 * Validation is on the RENDERED pixels, which is the only thing that matters to the segmenter — a file claiming
 * 600 DPI in its metadata but holding 900×900 pixels is not a card anyone can segment.
 */
export async function prepareCardImages(file: File): Promise<PreparedImage[]> {
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  const candidates: { file: File; page?: number }[] = isPdf
    ? (await pdfToPngs(file)).map((f, i) => ({ file: f, page: i + 1 }))
    : [{ file }];

  const prepared: PreparedImage[] = [];
  for (const c of candidates) {
    const { bitmap, width, height } = await measure(c.file);
    if (width < MIN_CARD_PX || height < MIN_CARD_PX) {
      bitmap.close?.();
      const inches = (n: number) => (n / REQUIRED_DPI).toFixed(1);
      throw new CardImageError(
        `${file.name}${c.page ? ` (page ${c.page})` : ''} is ${width}×${height} pixels — about ${inches(width)}" × ${inches(height)} at ${REQUIRED_DPI} DPI. ` +
          `A fingerprint card must be at least 8" × 8" scanned at ${REQUIRED_DPI} DPI (${MIN_CARD_PX}×${MIN_CARD_PX} pixels). ` +
          'Re-scan at 500 DPI or higher — most scanners do not offer 500 exactly, so choose the next one up, usually 600.',
      );
    }
    prepared.push({
      file: c.file,
      width,
      height,
      thumbnail: makeThumbnail(bitmap, width, height),
      ...(c.page ? { page: c.page } : {}),
    });
    bitmap.close?.();
  }
  return prepared;
}
